import { useMemo } from 'react';
import { emptySplitApi } from '@/store/emptyApi';

export interface AssessmentPeriodMetricResults {
  assessment_period_id: number;
  assessment_period_name?: string;
  assessment_period_status?: number;
  assessment_period_start?: string;
  assessment_period_end?: string;
  total_requests?: number;
  total_responses?: number;
  overall_score: number;
  pillars: Array<{
    label: string;
    factor?: number | null;
    average_response: number;
  }>;
}

interface MetricResultsArgs {
  assessmentPeriodId?: number;
}

interface MetricResultsApiRow {
  aggregate_type: number;
  assessment_period: number;
  result: AssessmentPeriodMetricResults;
}

export interface AssessmentPeriodMetricBenchmarkResults {
  assessment_period_id: number;
  benchmark_year: number;
  overall_score: {
    mean: number | null;
    median: number | null;
  };
  pillars: Array<{
    label: string;
    factor?: number | null;
    mean: number | null;
    median: number | null;
  }>;
}

interface MetricBenchmarkApiRow {
  aggregate_type: number;
  assessment_period: number;
  result: AssessmentPeriodMetricBenchmarkResults;
}

const metricResultsApi = emptySplitApi.injectEndpoints({
  endpoints: build => ({
    getAssessmentPeriodMetricResults: build.query<
      MetricResultsApiRow[],
      { assessmentPeriodId?: number }
    >({
      query: ({ assessmentPeriodId }) => ({
        url: '/api/portal/v1/assessments/metric-results/',
        params: assessmentPeriodId ? { assessment_period_id: assessmentPeriodId } : undefined,
      }),
    }),
    getAssessmentPeriodMetricBenchmarkResults: build.query<
      MetricBenchmarkApiRow,
      { assessmentPeriodId?: number }
    >({
      query: ({ assessmentPeriodId }) => ({
        url: '/api/portal/v1/assessments/metric-results/benchmark/',
        params: assessmentPeriodId ? { assessment_period_id: assessmentPeriodId } : undefined,
      }),
    }),
  }),
  overrideExisting: false,
});

const {
  useGetAssessmentPeriodMetricResultsQuery: useRawMetricResultsQuery,
  useGetAssessmentPeriodMetricBenchmarkResultsQuery: useRawMetricBenchmarkResultsQuery,
} = metricResultsApi;

const normalizeResults = (
  rows: MetricResultsApiRow[] | undefined,
  assessmentPeriodId?: number
): AssessmentPeriodMetricResults | undefined => {
  if (!Array.isArray(rows) || rows.length === 0) {
    return undefined;
  }

  const exactPeriodMatch = assessmentPeriodId
    ? rows.find(row => row?.assessment_period === assessmentPeriodId)
    : undefined;
  const row = exactPeriodMatch || rows[0];
  if (!row?.result) {
    return undefined;
  }

  const pillars = Array.isArray(row.result.pillars) ? row.result.pillars : [];
  if (pillars.length === 0) {
    return undefined;
  }

  return row.result;
};

const mapRowsToResults = (rows: MetricResultsApiRow[] | undefined): AssessmentPeriodMetricResults[] => {
  if (!Array.isArray(rows) || rows.length === 0) {
    return [];
  }

  return rows
    .map(row => row?.result)
    .filter((result): result is AssessmentPeriodMetricResults => Boolean(result));
};

export const useGetAssessmentPeriodMetricResultsQuery = (
  { assessmentPeriodId }: MetricResultsArgs,
  options?: { skip?: boolean }
) => {
  const query = useRawMetricResultsQuery(
    { assessmentPeriodId },
    { skip: options?.skip, refetchOnMountOrArgChange: true }
  );

  const data = useMemo(
    () => normalizeResults(query.data, assessmentPeriodId),
    [query.data, assessmentPeriodId]
  );

  return {
    ...query,
    data,
  };
};

export const useListAssessmentPeriodMetricResultsQuery = (
  args: MetricResultsArgs = {},
  options?: { skip?: boolean }
) => {
  const query = useRawMetricResultsQuery(
    { assessmentPeriodId: args.assessmentPeriodId },
    { skip: options?.skip, refetchOnMountOrArgChange: true }
  );

  const data = useMemo(() => mapRowsToResults(query.data), [query.data]);

  return {
    ...query,
    data,
  };
};

export const useGetAssessmentPeriodMetricBenchmarkResultsQuery = (
  { assessmentPeriodId }: MetricResultsArgs,
  options?: { skip?: boolean }
) => {
  const query = useRawMetricBenchmarkResultsQuery(
    { assessmentPeriodId },
    { skip: options?.skip, refetchOnMountOrArgChange: true }
  );

  const data = useMemo(
    () => query.data?.result,
    [query.data]
  );

  return {
    ...query,
    data,
  };
};
